import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useTranslation } from './hooks/useTranslation';
import { ToastContainer } from './components/ToastContainer';
import { VersionUpdateNotification } from './components/VersionUpdateNotification';
import { useVersionUpdate } from './hooks/useVersionUpdate';
import { Button } from './components/ui/button';

// Modern components
import ModernLayout from './layout/ModernLayout';
import ModernLogin from './layout/ModernLogin';
import ModernOrderList from './orders/ModernOrderList';
import ModernOrderCreate from './orders/ModernOrderCreate';
import ModernOrderEdit from './orders/ModernOrderEdit';
import ModernOrderShow from './orders/ModernOrderShow';
import ModernOrdersReport from './report/OrdersReport';
import ModernSalaryForecastReport from './report/SalaryForecastReport';
import ModernCampaignList from './campaigns/ModernCampaignList';
import ModernCampaignForm from './campaigns/ModernCampaignForm';
import ModernCampaignReport from './report/CampaignReport';
import ModernCostList from './costManagement/ModernCostList';
import ModernCostCreate from './costManagement/ModernCostCreate';
import ModernCostEdit from './costManagement/ModernCostEdit';
import ModernCostCategoryList from './costManagement/ModernCostCategoryList';
import ModernCostCategoryCreate from './costManagement/ModernCostCategoryCreate';
import ModernCostCategoryEdit from './costManagement/ModernCostCategoryEdit';
import ModernSettings from './settings/ModernSettings';
import ModernDashboard from './dashboard/ModernDashboard';
import { ModernInventoryManagement } from './inventory/ModernInventoryManagement';
import { ModernRecipeManagement } from './recipes/ModernRecipeManagement';
import { ModernRecipeFormPage } from './recipes/ModernRecipeFormPage';
import { ModernProductManagement } from './products/ModernProductManagement';
import ModernProductCategoryList from './products/ModernProductCategoryList';
import ModernProductCategoryForm from './products/ModernProductCategoryForm';
import ModernMenuList from './menus/ModernMenuList';
import ModernMenuForm from './menus/ModernMenuForm';
import { ModernIngredientManagement } from './ingredients/ModernIngredientManagement';
import { ModernCustomerManagement } from './customers/ModernCustomerManagement';
import ModernCustomerReviews from './reviews/ModernCustomerReviews';
import { ModernProductMappingManagement } from './grab/ModernProductMappingManagement';
import { ModernGrabIntegrationSettings } from './grab/ModernGrabIntegrationSettings';
import { ModernMenuSyncDashboard } from './grab/ModernMenuSyncDashboard';
import { ModernWarehouseManagement } from './warehouses/ModernWarehouseManagement';
import { ModernInventoryCategoryManagement } from './inventoryCategories/ModernInventoryCategoryManagement';
import { BugReportContextMenu } from './components/BugReportContextMenu';
import { BugReportDialog } from './components/BugReportDialog';
import { ErrorBoundary } from './components/ErrorBoundary';
import { globalErrorHandler } from './utils/globalErrorHandler';
import { LoadingSpinner } from './components/common/LoadingSpinner';

// Staff Features
import ModernStaffFeatures from './staffFeatures/ModernStaffFeatures';
import ModernStaffManagement from './staff/ModernStaffManagement';
import ModernShiftPlanning from './staffFeatures/ModernShiftPlanning';
import ModernAttendanceManagement from './staffFeatures/ModernAttendanceManagement';
import ModernBonusManagement from './bonuses/ModernBonusManagement';
import ModernPolicyManagement from './policies/ModernPolicyManagement';
import ModernKanbanBoard from './projectManagement/ModernKanbanBoard';
import ModernGanttChart from './projectManagement/ModernGanttChart';
// import ModernStaffProfileCreate from './staffFeatures/ModernStaffProfileCreate';
// import ModernStaffProfileEdit from './staffFeatures/ModernStaffProfileEdit';
// import ModernStaffPointsList from './staffFeatures/ModernStaffPointsList';
// import ModernStaffPointsCreate from './staffFeatures/ModernStaffPointsCreate';
// import ModernStaffPointsEdit from './staffFeatures/ModernStaffPointsEdit';
// import ModernStaffBadgeList from './staffFeatures/ModernStaffBadgeList';
// import ModernStaffBadgeCreate from './staffFeatures/ModernStaffBadgeCreate';
// import ModernStaffBadgeEdit from './staffFeatures/ModernStaffBadgeEdit';
// import ModernStaffRankingList from './staffFeatures/ModernStaffRankingList';
// import ModernStaffRankingCreate from './staffFeatures/ModernStaffRankingCreate';
// import ModernStaffRankingEdit from './staffFeatures/ModernStaffRankingEdit';
// import ModernPointRedemptionList from './staffFeatures/ModernPointRedemptionList';
// import ModernPointRedemptionCreate from './staffFeatures/ModernPointRedemptionCreate';
// import ModernPointRedemptionEdit from './staffFeatures/ModernPointRedemptionEdit';
// import ModernStaffRatingList from './staffFeatures/ModernStaffRatingList';
// import ModernStaffRatingCreate from './staffFeatures/ModernStaffRatingCreate';
// import ModernStaffRatingEdit from './staffFeatures/ModernStaffRatingEdit';
// import ModernCheckinCheckout from './staffFeatures/ModernCheckinCheckout';

// Auth
import authProvider from './authProvider';
import { useUserStore } from './stores/useUserStore';
import { useRBAC, Resource } from './hooks/useRBAC';
import { QRLoginConfirmPage } from './layout/QRLoginConfirmPage';

// Initialize input activity tracker early to prevent redirects during typing
// This import ensures the tracker starts monitoring user input immediately
import './services/inputActivityTracker';

// Theme is handled by Tailwind CSS and shadcn/ui

// Simple auth check
const checkAuthenticationStatus = async (): Promise<boolean> => {
  try {
    const auth = authProvider.auth;
    const authenticated = await auth.isAuthenticated();
    return authenticated;
  } catch {
    return false;
  }
};

// OAuth Callback Component
const OAuthCallback = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('OAuth callback started...');
        await authProvider.auth.onCallback();
        console.log('OAuth callback processed successfully');

        setTimeout(async () => {
          const authenticated = await checkAuthenticationStatus();
          console.log('Authentication status after callback:', authenticated);

          if (authenticated) {
            navigate('/dashboard');
          } else {
            navigate('/login');
          }
        }, 1000);
      } catch (error) {
        console.error('OAuth callback error:', error);
        navigate('/');
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <LoadingSpinner size="xl" variant="primary" className="mx-auto mb-4" />
        <p className="text-muted-foreground">
          {translate('pos.common.page.loading')}
        </p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { translate } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const { fetchUser, resetFetchAttempt } = useUserStore();

  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      setLoadingTimeout(true);
    }, 10000); // 10 seconds timeout

    const checkAuth = async () => {
      try {
        const authenticated = await checkAuthenticationStatus();

        if (authenticated) {
          resetFetchAttempt();
          try {
            await fetchUser();
          } catch (userError) {
            console.error('Error fetching user:', userError);
            // Don't block navigation if user fetch fails, but log it
          }
        }

        setIsAuthenticated(authenticated);
        setAuthError(null);
        clearTimeout(timeout);
      } catch (error) {
        console.error('Error during authentication check:', error);
        setAuthError(error instanceof Error ? error : new Error(String(error)));
        setIsAuthenticated(false);
        clearTimeout(timeout);
      }
    };
    checkAuth();

    return () => clearTimeout(timeout);
  }, [fetchUser, resetFetchAttempt]);

  // Show error if authentication check failed or timed out
  if (authError || (isAuthenticated === null && loadingTimeout)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full">
          <div className="bg-destructive/10 border border-destructive rounded-lg p-6">
            <h2 className="text-lg font-semibold text-destructive mb-2">
              {translate('errorBoundary.authError', 'Authentication Error')}
            </h2>
            <p className="text-muted-foreground mb-4">
              {authError
                ? authError.message
                : translate(
                    'errorBoundary.authTimeout',
                    'Authentication check timed out. Please try again.'
                  )}
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => window.location.reload()}
                variant="default"
              >
                {translate('errorBoundary.reload', 'Reload Page')}
              </Button>
              <Button
                onClick={() => (window.location.href = '/login')}
                variant="outline"
              >
                {translate('errorBoundary.goToLogin', 'Go to Login')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <LoadingSpinner size="xl" variant="primary" className="mx-auto mb-4" />
          <p className="text-muted-foreground">
            {translate('pos.common.page.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <ModernLayout>{children}</ModernLayout>;
};

// Protected Resource Route Component (checks both authentication and RBAC permissions)
const ProtectedResourceRoute = ({
  children,
  resource,
}: {
  children: React.ReactNode;
  resource: Resource;
}) => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const { fetchUser, resetFetchAttempt, user } = useUserStore();
  const { canAccessResource, highestRole } = useRBAC();

  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      setLoadingTimeout(true);
    }, 10000); // 10 seconds timeout

    const checkAuth = async () => {
      try {
        const authenticated = await checkAuthenticationStatus();

        if (authenticated) {
          resetFetchAttempt();
          try {
            await fetchUser();
          } catch (userError) {
            console.error('Error fetching user:', userError);
            // Don't block navigation if user fetch fails, but log it
          }
        }

        setIsAuthenticated(authenticated);
        setAuthError(null);
        clearTimeout(timeout);
      } catch (error) {
        console.error('Error during authentication check:', error);
        setAuthError(error instanceof Error ? error : new Error(String(error)));
        setIsAuthenticated(false);
        clearTimeout(timeout);
      }
    };
    checkAuth();

    return () => clearTimeout(timeout);
  }, [fetchUser, resetFetchAttempt]);

  // Show error if authentication check failed or timed out
  if (authError || (isAuthenticated === null && loadingTimeout)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full">
          <div className="bg-destructive/10 border border-destructive rounded-lg p-6">
            <h2 className="text-lg font-semibold text-destructive mb-2">
              {translate('errorBoundary.authError', 'Authentication Error')}
            </h2>
            <p className="text-muted-foreground mb-4">
              {authError
                ? authError.message
                : translate(
                    'errorBoundary.authTimeout',
                    'Authentication check timed out. Please try again.'
                  )}
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => window.location.reload()}
                variant="default"
              >
                {translate('errorBoundary.reload', 'Reload Page')}
              </Button>
              <Button
                onClick={() => (window.location.href = '/login')}
                variant="outline"
              >
                {translate('errorBoundary.goToLogin', 'Go to Login')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <LoadingSpinner size="xl" variant="primary" className="mx-auto mb-4" />
          <p className="text-muted-foreground">
            {translate('pos.common.page.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Special case for inventory: block staff even if they have list/view permissions
  // Staff can scan QR codes from dashboard but not access inventory management page
  if (resource === 'inventory' && highestRole === 'staff') {
    return (
      <ModernLayout>
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full">
            <div className="bg-destructive/10 border border-destructive rounded-lg p-6">
              <h2 className="text-lg font-semibold text-destructive mb-2">
                {translate('errorBoundary.accessDenied', 'Access Denied')}
              </h2>
              <p className="text-muted-foreground mb-4">
                {translate(
                  'errorBoundary.insufficientPermissions',
                  'You do not have permission to access this resource.'
                )}
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="default"
                >
                  {translate('errorBoundary.goToDashboard', 'Go to Dashboard')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ModernLayout>
    );
  }

  // Check RBAC permissions
  if (!canAccessResource(resource)) {
    return (
      <ModernLayout>
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full">
            <div className="bg-destructive/10 border border-destructive rounded-lg p-6">
              <h2 className="text-lg font-semibold text-destructive mb-2">
                {translate('errorBoundary.accessDenied', 'Access Denied')}
              </h2>
              <p className="text-muted-foreground mb-4">
                {translate(
                  'errorBoundary.insufficientPermissions',
                  'You do not have permission to access this resource.'
                )}
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="default"
                >
                  {translate('errorBoundary.goToDashboard', 'Go to Dashboard')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ModernLayout>
    );
  }

  return <ModernLayout>{children}</ModernLayout>;
};

// Auth Route Component (for login page)
const AuthRoute = ({ children }: { children: React.ReactNode }) => {
  const { translate } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      setLoadingTimeout(true);
    }, 10000); // 10 seconds timeout

    const checkAuth = async () => {
      try {
        const authenticated = await checkAuthenticationStatus();
        setIsAuthenticated(authenticated);
        setAuthError(null);
        clearTimeout(timeout);
      } catch (error) {
        console.error('Error during authentication check:', error);
        setAuthError(error instanceof Error ? error : new Error(String(error)));
        setIsAuthenticated(false);
        clearTimeout(timeout);
      }
    };
    checkAuth();

    return () => clearTimeout(timeout);
  }, []);

  // Show error if authentication check failed or timed out
  if (authError || (isAuthenticated === null && loadingTimeout)) {
    // Still show login page even if auth check fails
    return <>{children}</>;
  }

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <LoadingSpinner size="xl" variant="primary" className="mx-auto mb-4" />
          <p className="text-muted-foreground">
            {translate('pos.common.page.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const ModernApp = () => {
  const {
    hasNewVersion,
    showNotification,
    currentVersion,
    newVersion,
    isAutoRefreshing,
    autoRefreshDelay,
    handleRefresh,
    handleDelay,
  } = useVersionUpdate({
    checkInterval: 1000 * 60 * 5, // Check every 5 minutes
    idleTimeBeforeAutoRefresh: 1000 * 60 * 2, // 2 minutes of idle
    autoRefreshDelay: 10, // 10 seconds countdown
  });

  // Global bug report dialog state (accessible from anywhere, including error handler)
  const [globalBugReportOpen, setGlobalBugReportOpen] = React.useState(false);

  // Initialize global error handler and set up bug report trigger
  React.useEffect(() => {
    globalErrorHandler.initialize();

    // Import captureScreenshotWithPreview dynamically to avoid circular dependencies
    const triggerBugReport = async () => {
      console.log(
        '[BugReport] Trigger called, running preview sequence (dialog disabled)'
      );
      // Hide the error overlay if it exists
      const errorOverlay = document.getElementById('global-error-ui');
      if (errorOverlay) {
        errorOverlay.style.display = 'none';
      }
      try {
        const { captureScreenshotWithPreview } = await import(
          './utils/bugReportUtils'
        );
        await captureScreenshotWithPreview();
        console.log('[BugReport] Capture completed, opening dialog');
        setGlobalBugReportOpen(true);
      } catch (error) {
        console.error('[BugReport] Error capturing screenshot:', error);
      }
    };

    (window as any).__triggerBugReport = triggerBugReport;

    // Also listen for custom event as fallback
    const handleTriggerBugReport = async () => {
      console.log(
        '[BugReport] Custom event received, running preview sequence (dialog disabled)'
      );
      // Hide the error overlay if it exists
      const errorOverlay = document.getElementById('global-error-ui');
      if (errorOverlay) {
        errorOverlay.style.display = 'none';
      }
      try {
        const { captureScreenshotWithPreview } = await import(
          './utils/bugReportUtils'
        );
        await captureScreenshotWithPreview();
        console.log('[BugReport] Capture completed, opening dialog');
        setGlobalBugReportOpen(true);
      } catch (error) {
        console.error('[BugReport] Error capturing screenshot:', error);
      }
    };
    window.addEventListener('triggerBugReport', handleTriggerBugReport);

    // Verify the trigger is set up
    console.log(
      '[BugReport] Trigger function set up:',
      typeof (window as any).__triggerBugReport
    );

    return () => {
      delete (window as any).__triggerBugReport;
      window.removeEventListener('triggerBugReport', handleTriggerBugReport);
    };
  }, []);

  return (
    <ErrorBoundary>
      <ToastContainer />
      {/* Global Bug Report Dialog - accessible from error handler */}
      <BugReportDialog
        open={globalBugReportOpen}
        onClose={() => setGlobalBugReportOpen(false)}
      />
      <VersionUpdateNotification
        isOpen={showNotification}
        currentVersion={currentVersion}
        newVersion={newVersion}
        onRefresh={handleRefresh}
        onDelay={handleDelay}
        autoRefreshIn={isAutoRefreshing ? autoRefreshDelay : undefined}
      />
      <BugReportContextMenu>
        <Routes>
          {/* OAuth Callback Route */}
          <Route path="/oauth/callback" element={<OAuthCallback />} />

          {/* Auth Routes */}
          <Route
            path="/login"
            element={
              <AuthRoute>
                <ModernLogin />
              </AuthRoute>
            }
          />

          {/* QR Login Route - requires authentication */}
          <Route
            path="/qr-login/:sessionId"
            element={
              <ProtectedRoute>
                <QRLoginConfirmPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <ModernDashboard />
              </ProtectedRoute>
            }
          />

          {/* Order Routes */}
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <ModernOrderList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/create"
            element={
              <ProtectedRoute>
                <ModernOrderCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:id/edit"
            element={
              <ProtectedRoute>
                <ModernOrderEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:id/show"
            element={
              <ProtectedRoute>
                <ModernOrderShow />
              </ProtectedRoute>
            }
          />

          {/* Report Routes */}
          <Route
            path="/report/orders"
            element={
              <ProtectedResourceRoute resource="orders_report">
                <ModernOrdersReport />
              </ProtectedResourceRoute>
            }
          />
          <Route
            path="/report/salary-forecast"
            element={
              <ProtectedResourceRoute resource="salary_forecast">
                <ModernSalaryForecastReport />
              </ProtectedResourceRoute>
            }
          />
          <Route
            path="/report/campaigns/:campaignId"
            element={
              <ProtectedRoute>
                <ModernCampaignReport />
              </ProtectedRoute>
            }
          />

          {/* Campaign Routes */}
          <Route
            path="/campaigns"
            element={
              <ProtectedRoute>
                <ModernCampaignList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/create"
            element={
              <ProtectedRoute>
                <ModernCampaignForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/:id/edit"
            element={
              <ProtectedRoute>
                <ModernCampaignForm />
              </ProtectedRoute>
            }
          />

          {/* Product Routes */}
          <Route
            path="/products"
            element={
              <ProtectedRoute>
                <ModernProductManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/product_categories"
            element={
              <ProtectedRoute>
                <ModernProductCategoryList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/product_categories/create"
            element={
              <ProtectedRoute>
                <ModernProductCategoryForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/product_categories/:id/edit"
            element={
              <ProtectedRoute>
                <ModernProductCategoryForm />
              </ProtectedRoute>
            }
          />

          {/* Menu Routes */}
          <Route
            path="/menus"
            element={
              <ProtectedResourceRoute resource="menus">
                <ModernMenuList />
              </ProtectedResourceRoute>
            }
          />
          <Route
            path="/menus/create"
            element={
              <ProtectedResourceRoute resource="menus">
                <ModernMenuForm />
              </ProtectedResourceRoute>
            }
          />
          <Route
            path="/menus/:id/edit"
            element={
              <ProtectedResourceRoute resource="menus">
                <ModernMenuForm />
              </ProtectedResourceRoute>
            }
          />

          {/* Inventory Routes */}
          <Route
            path="/inventory"
            element={
              <ProtectedResourceRoute resource="inventory">
                <ModernInventoryManagement />
              </ProtectedResourceRoute>
            }
          />

          {/* Warehouse Routes */}
          <Route
            path="/warehouses"
            element={
              <ProtectedRoute>
                <ModernWarehouseManagement />
              </ProtectedRoute>
            }
          />

          {/* Inventory Category Routes */}
          <Route
            path="/inventory_categories"
            element={
              <ProtectedRoute>
                <ModernInventoryCategoryManagement />
              </ProtectedRoute>
            }
          />

          {/* Ingredient Routes */}
          <Route
            path="/ingredients"
            element={
              <ProtectedResourceRoute resource="ingredients">
                <ModernIngredientManagement />
              </ProtectedResourceRoute>
            }
          />

          {/* Recipe Routes */}
          <Route
            path="/recipes"
            element={
              <ProtectedRoute>
                <ModernRecipeManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recipes/create"
            element={
              <ProtectedRoute>
                <ModernRecipeFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recipes/:id/edit"
            element={
              <ProtectedRoute>
                <ModernRecipeFormPage />
              </ProtectedRoute>
            }
          />

          {/* Cost Management Routes */}
          <Route
            path="/costs"
            element={
              <ProtectedResourceRoute resource="costs">
                <ModernCostList />
              </ProtectedResourceRoute>
            }
          />
          <Route
            path="/costs/create"
            element={
              <ProtectedRoute>
                <ModernCostCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/costs/:id/edit"
            element={
              <ProtectedResourceRoute resource="costs">
                <ModernCostEdit />
              </ProtectedResourceRoute>
            }
          />
          <Route
            path="/cost_categories"
            element={
              <ProtectedResourceRoute resource="cost_categories">
                <ModernCostCategoryList />
              </ProtectedResourceRoute>
            }
          />
          <Route
            path="/cost_categories/create"
            element={
              <ProtectedResourceRoute resource="cost_categories">
                <ModernCostCategoryCreate />
              </ProtectedResourceRoute>
            }
          />
          <Route
            path="/cost_categories/:id/edit"
            element={
              <ProtectedResourceRoute resource="cost_categories">
                <ModernCostCategoryEdit />
              </ProtectedResourceRoute>
            }
          />

          {/* Staff Features Routes */}
          <Route
            path="/staff_profiles"
            element={
              <ProtectedRoute>
                <ModernStaffFeatures />
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff_management"
            element={
              <ProtectedRoute>
                <ModernStaffManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shift_planning"
            element={
              <ProtectedRoute>
                <ModernShiftPlanning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance_management"
            element={
              <ProtectedRoute>
                <ModernAttendanceManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bonuses"
            element={
              <ProtectedRoute>
                <ModernBonusManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/policies"
            element={
              <ProtectedResourceRoute resource="policies">
                <ModernPolicyManagement />
              </ProtectedResourceRoute>
            }
          />

          {/* Project Management Routes */}
          <Route
            path="/project-management/kanban"
            element={
              <ProtectedResourceRoute resource="project_management">
                <ModernKanbanBoard />
              </ProtectedResourceRoute>
            }
          />
          <Route
            path="/project-management/gantt"
            element={
              <ProtectedResourceRoute resource="project_management">
                <ModernGanttChart />
              </ProtectedResourceRoute>
            }
          />
          {/* <Route
          path="/staff_profiles/create"
          element={
            <ProtectedRoute>
              <ModernStaffProfileCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff_profiles/:id/edit"
          element={
            <ProtectedRoute>
              <ModernStaffProfileEdit />
            </ProtectedRoute>
          }
        /> */}

          {/* <Route
          path="/staff_points"
          element={
            <ProtectedRoute>
              <ModernStaffPointsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff_points/create"
          element={
            <ProtectedRoute>
              <ModernStaffPointsCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff_points/:id/edit"
          element={
            <ProtectedRoute>
              <ModernStaffPointsEdit />
            </ProtectedRoute>
          }
        />

        <Route
          path="/staff_badges"
          element={
            <ProtectedRoute>
              <ModernStaffBadgeList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff_badges/create"
          element={
            <ProtectedRoute>
              <ModernStaffBadgeCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff_badges/:id/edit"
          element={
            <ProtectedRoute>
              <ModernStaffBadgeEdit />
            </ProtectedRoute>
          }
        />

        <Route
          path="/staff_rankings"
          element={
            <ProtectedRoute>
              <ModernStaffRankingList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff_rankings/create"
          element={
            <ProtectedRoute>
              <ModernStaffRankingCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff_rankings/:id/edit"
          element={
            <ProtectedRoute>
              <ModernStaffRankingEdit />
            </ProtectedRoute>
          }
        />

        <Route
          path="/point_redemptions"
          element={
            <ProtectedRoute>
              <ModernPointRedemptionList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/point_redemptions/create"
          element={
            <ProtectedRoute>
              <ModernPointRedemptionCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/point_redemptions/:id/edit"
          element={
            <ProtectedRoute>
              <ModernPointRedemptionEdit />
            </ProtectedRoute>
          }
        /> */}

          {/* <Route
          path="/staff_ratings"
          element={
            <ProtectedRoute>
              <ModernStaffRatingList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff_ratings/create"
          element={
            <ProtectedRoute>
              <ModernStaffRatingCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff_ratings/:id/edit"
          element={
            <ProtectedRoute>
              <ModernStaffRatingEdit />
            </ProtectedRoute>
          }
        />

        <Route
          path="/checkin-checkout"
          element={
            <ProtectedRoute>
              <ModernCheckinCheckout />
            </ProtectedRoute>
          }
        />

        {/* Customer Routes */}
          <Route
            path="/customers"
            element={
              <ProtectedResourceRoute resource="customers">
                <ModernCustomerManagement />
              </ProtectedResourceRoute>
            }
          />
          <Route
            path="/customer_reviews"
            element={
              <ProtectedRoute>
                <ModernCustomerReviews />
              </ProtectedRoute>
            }
          />

          {/* Settings Route */}
          <Route
            path="/settings"
            element={
              <ProtectedResourceRoute resource="settings">
                <ModernSettings />
              </ProtectedResourceRoute>
            }
          />

          {/* Grab Integration Routes */}
          <Route
            path="/grab/mappings"
            element={
              <ProtectedRoute>
                <ModernProductMappingManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/grab/settings"
            element={
              <ProtectedRoute>
                <ModernGrabIntegrationSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/grab/sync"
            element={
              <ProtectedRoute>
                <ModernMenuSyncDashboard />
              </ProtectedRoute>
            }
          />

          {/* Catch all route - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BugReportContextMenu>
    </ErrorBoundary>
  );
};

export default ModernApp;
